import React, { useEffect, useRef, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { LanguageSelector } from "./LanguageSelector";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { formatApiError } from "../lib/perf";
import "./hitmax-auth.css";

const NATIVE_APP = (() => { try { return !!window.Capacitor?.isNativePlatform?.(); } catch (e) { return false; } })();
const IOS_APP = (() => { try { return NATIVE_APP && window.Capacitor?.getPlatform?.() === "ios"; } catch (e) { return false; } })();

const reduced = () => { try { return window.matchMedia("(prefers-reduced-motion: reduce)").matches; } catch (e) { return false; } };

const AppleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M16.37 12.6c-.02-2.06 1.68-3.05 1.76-3.1-0.96-1.4-2.45-1.6-2.98-1.62-1.27-.13-2.48.75-3.12.75-.64 0-1.64-.73-2.7-.71-1.39.02-2.67.81-3.38 2.05-1.44 2.5-.37 6.2 1.04 8.23.69.99 1.51 2.1 2.58 2.06 1.04-.04 1.43-.67 2.69-.67 1.25 0 1.61.67 2.7.65 1.12-.02 1.83-1.01 2.51-2.01.79-1.15 1.12-2.27 1.14-2.33-.03-.01-2.19-.84-2.21-3.32zM14.33 6.44c.57-.69.95-1.65.85-2.61-.82.03-1.81.55-2.4 1.24-.53.61-.99 1.58-.87 2.51.91.07 1.85-.46 2.42-1.14z" />
  </svg>
);

const AppleButton = () => {
  const { t } = useTranslation();
  const { loginWithAppleNative } = useAuth() || {};
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/auth/apple/available")
      .then((r) => setOk(!!r.data?.available && (!IOS_APP || !!r.data?.native)))
      .catch(() => {});
  }, []);
  // App iOS : le bouton n'apparaît que si le serveur gère la connexion native (sinon : e-mail uniquement).
  if (IOS_APP && !ok) return null;
  const go = async () => {
    if (!ok) { toast.info(t("auth.apple_soon")); return; }
    if (!IOS_APP) { window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/apple/start`; return; }
    // App iOS : fenêtre système d'Apple (Face ID), sans quitter l'app.
    if (busy) return;
    setBusy(true);
    try {
      await loginWithAppleNative();
    } catch (err) {
      const canceled = /cancel|1001/i.test(String(err?.message || err?.code || ""));
      if (!canceled) toast.error(err?.response?.data?.detail != null
        ? formatApiError(err.response.data.detail) : t("auth.apple_error"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <button type="button" className="hm2-btn hm2-apple" data-testid="auth-apple" onClick={go}>
      <AppleIcon /> {t("auth.apple")}
    </button>
  );
};

const Ribbon = () => {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current; if (!c) return; const ctx = c.getContext("2d");
    const still = reduced();
    let raf = 0, t = 0, w = 0, h = 0;
    const ROWS = 6, STEP = 15; // depth layers + horizontal density
    const resize = () => { w = c.clientWidth; h = c.clientHeight; c.width = w * 2; c.height = h * 2; ctx.setTransform(2, 0, 0, 2, 0, 0); };
    resize(); window.addEventListener("resize", resize);

    // green (left) -> gold/orange (right), matching the dashboard mesh
    const colorAt = (fx) => {
      // fx 0..1 across width; stops: green -> lime -> gold -> amber
      const stops = [
        [0.00, [40, 230, 120]],
        [0.45, [150, 220, 70]],
        [0.72, [230, 190, 70]],
        [1.00, [235, 150, 40]],
      ];
      let a = stops[0], b = stops[stops.length - 1];
      for (let i = 0; i < stops.length - 1; i++) { if (fx >= stops[i][0] && fx <= stops[i + 1][0]) { a = stops[i]; b = stops[i + 1]; break; } }
      const k = (fx - a[0]) / (b[0] - a[0] || 1);
      return [Math.round(a[1][0] + (b[1][0] - a[1][0]) * k), Math.round(a[1][1] + (b[1][1] - a[1][1]) * k), Math.round(a[1][2] + (b[1][2] - a[1][2]) * k)];
    };

    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      const cols = Math.max(8, Math.floor(w / STEP));
      // build point grid: row 0 = back (top, small), last row = front (bottom, big)
      const grid = [];
      for (let r = 0; r < ROWS; r++) {
        const depth = r / (ROWS - 1);                       // 0 back .. 1 front
        const baseY = h * (0.16 + depth * 0.78);            // spread vertically
        const rowScale = 0.35 + depth * 0.65;               // perspective compression at back
        const line = [];
        for (let i = 0; i <= cols; i++) {
          const fx = i / cols;
          const x = fx * w;
          const amp = (5 + depth * 16) * rowScale;
          const wobble = Math.sin(fx * 6.2 + t * 0.9 + r * 0.5) * amp
                       + Math.sin(fx * 13.0 - t * 1.3 + r) * amp * 0.4;
          const y = baseY - wobble;
          line.push({ x, y, fx, depth });
        }
        grid.push(line);
      }
      // connecting lines (mesh) — faint
      for (let r = 0; r < ROWS; r++) {
        const line = grid[r];
        for (let i = 0; i < line.length - 1; i++) {
          const p = line[i], q = line[i + 1];
          const col = colorAt(p.fx);
          ctx.strokeStyle = `rgba(${col[0]},${col[1]},${col[2]},${0.05 + p.depth * 0.22})`;
          ctx.lineWidth = 0.5 + p.depth * 0.9;
          ctx.beginPath(); ctx.moveTo(p.x, p.y); ctx.lineTo(q.x, q.y); ctx.stroke();
          if (r < ROWS - 1 && i % 1 === 0) {
            const d = grid[r + 1][i];
            ctx.strokeStyle = `rgba(${col[0]},${col[1]},${col[2]},${0.03 + p.depth * 0.10})`;
            ctx.beginPath(); ctx.moveTo(p.x, p.y); ctx.lineTo(d.x, d.y); ctx.stroke();
          }
        }
      }
      // glowing dots
      for (let r = 0; r < ROWS; r++) {
        const line = grid[r];
        for (let i = 0; i < line.length; i++) {
          const p = line[i];
          const col = colorAt(p.fx);
          const rad = 0.7 + p.depth * 1.9;
          const a = 0.25 + p.depth * 0.6;
          ctx.beginPath(); ctx.arc(p.x, p.y, rad, 0, 6.283);
          ctx.fillStyle = `rgba(${col[0]},${col[1]},${col[2]},${a})`;
          ctx.shadowColor = `rgba(${col[0]},${col[1]},${col[2]},${a})`;
          ctx.shadowBlur = 3 + p.depth * 6;
          ctx.fill();
        }
      }
      ctx.shadowBlur = 0;
      if (!still) { t += 0.02; raf = requestAnimationFrame(draw); }
    };
    draw();
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, []);
  return <canvas ref={ref} className="hm2-ribbon" data-testid="hm2-ribbon" />;
};

const useCounter = (target, dur, fmt) => {
  const [v, setV] = useState(0);
  useEffect(() => {
    if (reduced()) { setV(target); return; }
    let raf = 0, start = null;
    const step = (ts) => { if (!start) start = ts; const p = Math.min((ts - start) / dur, 1); const e = 1 - Math.pow(1 - p, 3); setV(target * e); if (p < 1) raf = requestAnimationFrame(step); };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, dur]);
  return fmt ? fmt(v) : v;
};

const STATS_CACHE_KEY = "hitmax_marketing_v2";
// Audit conformité F7 : uniquement des chiffres réels. Aucune valeur de repli inventée :
// on réutilise le dernier état réel connu (cache), sinon le bloc reste masqué.
const realNumber = (v) => (typeof v === "number" && v > 0 ? v : null);

const readStatsCache = () => {
  try { return JSON.parse(localStorage.getItem(STATS_CACHE_KEY) || "null"); } catch (e) { return null; }
};

const Stats = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(readStatsCache);
  useEffect(() => {
    try { localStorage.removeItem("hitmax_marketing_v1"); } catch (e) {}  // ancien cache mêlant valeurs fictives
    api.get("/performance/track-record").then((r) => {
      const mk = r.data?.marketing || {};
      const next = {
        sample: realNumber(r.data?.sample),
        bookmakers_compared: realNumber(mk.bookmakers_compared),
        leagues_tracked: realNumber(mk.leagues_tracked),
        sports_covered: realNumber(mk.sports_covered),
      };
      setData(next);
      try { localStorage.setItem(STATS_CACHE_KEY, JSON.stringify(next)); } catch (e) {}
    }).catch(() => {}); // en cas d'échec on garde le dernier état réel connu (ou rien)
  }, []);
  if (!data) return null;

  const tiles = [
    { testid: "hm2-stat-volume", raw: data.sample, v: `${data.sample}+`, lbl: t("auth.stat_generated") },
    { testid: "hm2-stat-bookmakers", raw: data.bookmakers_compared, v: data.bookmakers_compared, lbl: t("auth.stat_bookmakers") },
    { testid: "hm2-stat-leagues", raw: data.leagues_tracked, v: data.leagues_tracked, lbl: t("auth.stat_leagues"), sub: t("auth.stat_leagues_sub") },
    { testid: "hm2-stat-sports", raw: data.sports_covered, v: data.sports_covered, lbl: t("auth.stat_sports") },
  ].filter((s) => s.raw);
  if (!tiles.length) return null;

  return (
    <div className="hm2-stats" data-testid="hm2-stats" style={{ gridTemplateColumns: `repeat(${tiles.length}, 1fr)` }}>
      {tiles.map((s) => (
        <div key={s.testid} className="hm2-stat" data-testid={s.testid}>
          <div className="v">{s.v}</div>
          <div className="lbl">{s.lbl}</div>
          {s.sub && <div className="sub">{s.sub}</div>}
        </div>
      ))}
    </div>
  );
};

export const HitmaxAuth = (p) => {
  const { t } = useTranslation();
  const { mode, setMode, firstName, setFirstName, name, setName, email, setEmail, password, setPassword, loading, error, forgot, setForgot, sent, setSent, partnerCode, setPartnerCode, setError, submit, handleGoogle, GoogleIcon, acceptTerms, setAcceptTerms, notice, resendActivation } = p;
  const [showPw, setShowPw] = useState(false);
  const showForm = !!mode || forgot;
  return (
    <div className="hm2" data-testid="hitmax-auth">
      <div className="hm2-langbar"><LanguageSelector variant="header" /></div>
      {/* LEFT */}
      <div className="hm2-left">
        <div className="hm2-samurai" aria-hidden="true" data-testid="hm2-samurai" style={{ backgroundImage: `url(${process.env.PUBLIC_URL || ""}/samurai-bg.jpg)`, backgroundSize: "cover", backgroundPosition: "center 53%", backgroundRepeat: "no-repeat" }} />
        <div className="hm2-samurai-eyes" aria-hidden="true" style={{ backgroundImage: `url(${process.env.PUBLIC_URL || ""}/samurai-eyes.png)`, backgroundSize: "cover", backgroundPosition: "center 53%", backgroundRepeat: "no-repeat" }} />
        <div className="hm2-eyes" aria-hidden="true" />
        <div className="hm2-grid" aria-hidden="true" />
        <span className="hm2-hook tl" /><span className="hm2-hook tr" /><span className="hm2-hook bl" /><span className="hm2-hook br" />
        <div className="hm2-lc">
          <div className="hm2-kicker">{t("auth.kicker")}</div>
          <img
            src={`${process.env.PUBLIC_URL || ""}/hitmax-logo-connexion.png`}
            alt="HITMAX"
            data-testid="hm2-logo"
            style={{ width: "min(82%, 380px)", display: "block", margin: "12px auto 8px", filter: "drop-shadow(0 4px 18px rgba(0,0,0,0.55)) drop-shadow(0 0 22px rgba(217,179,106,0.18))" }}
          />
          <div className="hm2-tag">{t("auth.tag")}</div>
          <div className="hm2-split"><span className="g">{t("auth.split_data")}</span><span className="o">{t("auth.split_edge")}</span></div>
          <div className="hm2-balls" data-testid="hm2-balls">
            <div className="hm2-beam" />
            <div className="hm2-ball b1"><div className="halo" style={{ background: "rgba(23,227,126,0.30)" }} /><div className="rim" /><img className="hm2-ballimg" src={`${process.env.PUBLIC_URL || ""}/ball-soccer-192.png`} alt="Football" /></div>
            <div className="hm2-ball b2"><div className="halo" style={{ background: "rgba(200,255,53,0.30)" }} /><div className="rim" /><img className="hm2-ballimg" src={`${process.env.PUBLIC_URL || ""}/ball-tennis-192.png`} alt="Tennis" /></div>
            <div className="hm2-ball b3"><div className="halo" style={{ background: "rgba(224,122,47,0.38)" }} /><div className="rim" /><img className="hm2-ballimg" src={`${process.env.PUBLIC_URL || ""}/ball-basket-192.png`} alt="Basketball" /></div>
          </div>
        </div>
        <Ribbon />
        <Stats />
      </div>

      {/* RIGHT */}
      <div className="hm2-right">
        <div className="hm2-form">
          {forgot ? (
            <>
              <h2 className="hm2-h2">{t("auth.forgot_title")}</h2>
              <p className="hm2-sub">{t("auth.forgot_sub")}</p>
              {sent ? (
                <div data-testid="forgot-confirmation">
                  <p className="hm2-sub"><Trans i18nKey="auth.forgot_sent" values={{ email }} components={{ b: <b /> }} /></p>
                  <button className="hm2-back" data-testid="forgot-back" onClick={() => { setForgot(false); setSent(false); setError(""); }}>{t("common.back")}</button>
                </div>
              ) : (
                <form onSubmit={submit}>
                  <input className="hm2-field" data-testid="forgot-email" type="email" placeholder={t("auth.email")} value={email} onChange={(e) => setEmail(e.target.value)} required />
                  {error && <p className="hm2-err" data-testid="auth-error">{error}</p>}
                  <button type="submit" className="hm2-btn hm2-primary" data-testid="forgot-submit" disabled={loading}>{loading ? "..." : t("auth.forgot_submit")}</button>
                  <button type="button" className="hm2-back" data-testid="forgot-cancel" onClick={() => { setForgot(false); setError(""); }}>{t("common.cancel")}</button>
                </form>
              )}
            </>
          ) : (
            <>
              <h2 className="hm2-h2">{t("auth.hero_title_pre")}<span className="ia">{t("auth.ai_acronym")}</span></h2>
              <p className="hm2-sub">{t("auth.hero_sub_1")}<br />{t("auth.hero_sub_2")}</p>

              {/* Web : Google et Apple. App iOS : Apple NATIF uniquement (fenêtre système, sans navigateur).
                  App Android : e-mail uniquement, tant que la connexion Google native n'est pas en place. */}
              {(!NATIVE_APP || IOS_APP) && (
                <>
                  {!NATIVE_APP && <button type="button" className="hm2-btn hm2-google" data-testid="auth-google" onClick={handleGoogle}><GoogleIcon /> {t("auth.google")}</button>}
                  <AppleButton />
                  <div className="hm2-sep"><span className="ln" /><span>{t("common.or")}</span><span className="ln" /></div>
                </>
              )}

              {!showForm && (
                <>
                  <button type="button" className="hm2-btn hm2-gold hero-cta-pulse" data-testid="auth-tab-signup" onClick={() => { setMode("signup"); setError(""); }}>{t("auth.see_free")}</button>
                  <div className="hm2-reassure"><span><span className="ck">✓</span> {t("auth.reassure_fast")}</span></div>
                  <div className="hm2-sep"><span className="ln" /><span>{t("auth.already_member")}</span><span className="ln" /></div>
                  <button type="button" className="hm2-btn hm2-secondary" data-testid="auth-tab-login" onClick={() => { setMode("login"); setError(""); }}>{t("auth.login")}</button>
                  <button type="button" className="hm2-forgot" data-testid="forgot-link-landing" onClick={() => { setForgot(true); setError(""); }}>{t("auth.forgot_link")}</button>
                </>
              )}

              {mode && (
                <form onSubmit={submit} style={{ marginTop: 4 }}>
                  {mode === "signup" && <input className="hm2-field" data-testid="auth-firstname" placeholder={t("auth.firstname")} value={firstName} onChange={(e) => setFirstName(e.target.value)} required />}
                  {mode === "signup" && <input className="hm2-field" data-testid="auth-name" placeholder={t("auth.lastname")} value={name} onChange={(e) => setName(e.target.value)} required />}
                  <input className="hm2-field" data-testid="auth-email" type="email" placeholder={t("auth.email")} value={email} onChange={(e) => setEmail(e.target.value)} required />
                  <div className="hm2-pw">
                    <input className="hm2-field" data-testid="auth-password" type={showPw ? "text" : "password"} placeholder={t("auth.password")} value={password} onChange={(e) => setPassword(e.target.value)} required />
                    <button type="button" className="hm2-pw-toggle" data-testid="auth-password-toggle" onClick={() => setShowPw((s) => !s)} aria-label={showPw ? t("auth.hide_password") : t("auth.show_password")} title={showPw ? t("auth.hide_password") : t("auth.show_password")}>
                      {showPw ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                      )}
                    </button>
                  </div>
                  {error && <p className="hm2-err" data-testid="auth-error">{error}</p>}
                  {notice && (
                    <div className="text-left text-[0.8rem] leading-snug" style={{ color: "var(--muted)" }} role="status" data-testid="auth-notice">
                      <p>{notice}</p>
                      {mode === "login" && email && (
                        <button type="button" className="underline mt-1" onClick={resendActivation} data-testid="auth-resend-activation">
                          {t("auth.resend_activation")}
                        </button>
                      )}
                    </div>
                  )}
                  {mode === "signup" && (
                    <input className="hm2-field" data-testid="auth-partner-code"
                      placeholder={t("auth.partner_code_placeholder")}
                      value={partnerCode || ""} onChange={(e) => setPartnerCode(e.target.value)}
                      style={{ opacity: 0.8, fontSize: "0.85rem" }} />
                  )}
                  {mode === "signup" && (
                    <label className="flex items-start gap-2.5 text-left text-[0.8rem] leading-snug cursor-pointer" style={{ color: "var(--muted)" }}>
                      <input type="checkbox" checked={!!acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)}
                        className="mt-0.5 shrink-0" data-testid="auth-accept-terms" required />
                      <span>
                        {t("auth.terms_prefix")}{" "}
                        <a href="/cgu" target="_blank" rel="noopener noreferrer" className="underline">{t("auth.terms_cgu")}</a>
                        {" "}{t("auth.terms_and")}{" "}
                        <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline">{t("auth.terms_privacy")}</a>.
                      </span>
                    </label>
                  )}
                  <button type="submit" className="hm2-btn hm2-primary" data-testid="auth-submit" disabled={loading}>{loading ? "..." : (mode === "login" ? t("auth.submit_login") : t("auth.submit_signup"))}</button>
                  <button type="button" className="hm2-back" data-testid="auth-change" onClick={() => { setMode(null); setError(""); }}>{t("common.back")}</button>
                </form>
              )}

              {mode === "login" && (
                <button className="hm2-forgot" data-testid="forgot-link" onClick={() => { setForgot(true); setError(""); }}>{t("auth.forgot_link")}</button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default HitmaxAuth;
